import React from "react";
import { ChatText, CheckCircle, Clock, EnvelopeSimple, MapPin, Phone } from "@phosphor-icons/react";
import { ThankYouTestimonials } from "../BuyerTestimonials";
import { NO_FEE_DEFAULT, fillFirm } from "@/lib/sharedSectionDefaults";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
export const absUrl = (u) => (u && u.startsWith("/api/") ? `${BACKEND}${u}` : u);
const digits = (p) => (p || "").replace(/[^0-9+]/g, "");
const H2 = "lp-serif text-2xl font-bold text-[#0a1929] sm:text-3xl";

export function embedVideo(u) {
  const yt = u.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/);
  if (yt) return { kind: "iframe", src: `https://www.youtube.com/embed/${yt[1]}` };
  const vm = u.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vm) return { kind: "iframe", src: `https://player.vimeo.com/video/${vm[1]}` };
  const ws = u.match(/(?:wistia\.(?:com|net)|wi\.st)\/(?:medias|embed\/iframe)\/(\w+)/);
  if (ws) return { kind: "iframe", src: `https://fast.wistia.net/embed/iframe/${ws[1]}` };
  if (/^https:\/\/.+\.(mp4|webm|mov)(\?|$)/i.test(u)) return { kind: "video", src: u };
  return null;
}

const Section = ({ children, testId, className = "" }) => (
  <section className={`mx-auto w-full max-w-4xl px-5 py-8 sm:py-10 ${className}`} data-testid={testId}>{children}</section>
);

const Hero = ({ b, ctx }) => (
  <Section testId="ty-block-hero" className="pt-12 text-center sm:pt-16">
    <div className="mx-auto grid h-14 w-14 place-items-center rounded-full text-white" style={{ background: ctx.accent }}>
      <CheckCircle size={32} weight="fill" />
    </div>
    <h1 className="lp-serif mt-5 text-4xl font-bold leading-tight text-[#0a1929] sm:text-5xl" data-testid="ty-headline">
      {b.headline ? fillFirm(b.headline, ctx.firm, "our team") : ctx.headline}
    </h1>
    <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-slate-600" data-testid="ty-message">
      {b.message ? fillFirm(b.message, ctx.firm, "our team") : ctx.message}
    </p>
    {ctx.reference && (
      <div className="mt-5 inline-block rounded-full border border-slate-300 bg-white px-4 py-1.5 font-mono text-xs" data-testid="ty-reference">
        Case reference: {ctx.reference}
      </div>
    )}
  </Section>
);

const NextStepsBlock = ({ b, ctx }) => {
  const steps = b.steps?.length ? b.steps : ctx.timeline || [];
  if (!steps.length) return null;
  return (
    <Section testId="ty-block-next-steps">
      <h2 className={H2}>{b.title || "What happens next"}</h2>
      <div className={`mt-6 grid gap-4 ${ctx.compact ? "" : "md:grid-cols-3"}`}>
        {steps.map((s, i) => (
          <div key={i} className="overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid={`ty-step-${i}`}>
            {s.image_url && <img src={absUrl(s.image_url)} alt="" className="h-40 w-full object-cover" />}
            <div className="p-5">
              <div className="grid h-8 w-8 place-items-center rounded-full text-sm font-bold text-white" style={{ background: ctx.accent }}>{i + 1}</div>
              <div className="mt-3 font-bold text-[#0a1929]">{s.title}</div>
              {s.body && <p className="mt-1 text-sm leading-relaxed text-slate-600">{s.body}</p>}
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
};

const Row = ({ icon: Icon, children, testId }) => (
  <div className="flex items-start gap-2 text-sm text-slate-700" data-testid={testId}>
    <Icon size={16} className="mt-0.5 shrink-0 text-slate-500" />{children}
  </div>
);

const CallButtons = ({ ctx }) => (
  <div className="mt-5 flex flex-col gap-3 sm:flex-row">
    <a href={`tel:${digits(ctx.phone)}`} data-testid="ty-call-btn" style={{ background: ctx.accent }}
       className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-full px-6 font-bold text-white transition-transform duration-200 hover:-translate-y-0.5">
      <Phone size={18} weight="fill" /> Call {ctx.phone}
    </a>
    <a href={`sms:${digits(ctx.phone)}`} data-testid="ty-text-btn"
       className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-full border-2 border-[#0a1929] px-6 font-bold text-[#0a1929] transition-transform duration-200 hover:-translate-y-0.5">
      <ChatText size={18} weight="fill" /> Text us
    </a>
  </div>
);

const FirmContact = ({ b, ctx }) => {
  const c = ctx.contact || {};
  return (
    <Section testId="ty-block-firm-contact">
      <div className="rounded-2xl border-2 border-[#0a1929] bg-white p-6 sm:p-8">
        <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">{b.title || "Who will contact you"}</div>
        <div className="mt-3 flex flex-wrap items-center gap-4">
          {ctx.logoUrl && <img src={ctx.logoUrl} alt={ctx.firm || ""} className="h-12 w-auto max-w-[160px] object-contain" data-testid="ty-firm-logo" />}
          <div className="lp-serif text-2xl font-bold text-[#0a1929]" data-testid="ty-firm-name">{ctx.firm || ctx.platformName}</div>
        </div>
        <div className="mt-4 grid gap-2 sm:grid-cols-2">
          {ctx.showCall && <Row icon={Phone} testId="ty-contact-phone"><a href={`tel:${digits(ctx.phone)}`} className="font-semibold underline">{ctx.phone}</a></Row>}
          {c.email && <Row icon={EnvelopeSimple} testId="ty-contact-email"><a href={`mailto:${c.email}`} className="underline">{c.email}</a></Row>}
          {c.address && <Row icon={MapPin} testId="ty-contact-address"><span className="whitespace-pre-line">{c.address}</span></Row>}
          {c.hours && <Row icon={Clock} testId="ty-contact-hours">{c.hours}</Row>}
        </div>
        {ctx.showCall && <CallButtons ctx={ctx} />}
        <p className="mt-4 text-xs text-slate-500">Keep your phone nearby — they&apos;ll reach out at the number you gave us.</p>
      </div>
    </Section>
  );
};

const Video = ({ b, ctx }) => {
  const v = embedVideo(ctx.videoUrl || b.url || "");
  if (!v) return null;
  return (
    <Section testId="ty-block-video">
      <h2 className={H2}>{b.title || "A message from your team"}</h2>
      <div className="mt-5 aspect-video overflow-hidden rounded-2xl bg-black">
        {v.kind === "iframe"
          ? <iframe src={v.src} title={b.title || "Video"} allow="autoplay; fullscreen; picture-in-picture" allowFullScreen className="h-full w-full" data-testid="ty-video" />
          : <video src={v.src} controls className="h-full w-full" data-testid="ty-video" />}
      </div>
    </Section>
  );
};

const Testimonials = ({ b, ctx }) => (
  <Section testId="ty-block-testimonials"><ThankYouTestimonials code={ctx.buyerCode} firm={ctx.firm} title={b.title} wide /></Section>
);

const NoFee = ({ b, ctx }) => {
  const nf = ctx.shared?.no_fee || NO_FEE_DEFAULT;
  const items = b.items?.length ? b.items : nf.items;
  return (
    <Section testId="ty-block-no-fee">
      <div className="rounded-2xl bg-[#0a1929] p-7 text-white sm:p-9">
        <div className="lp-serif text-2xl font-bold sm:text-3xl">{b.title || nf.title}</div>
        <div className={`mt-5 grid gap-3 ${ctx.compact ? "" : "sm:grid-cols-3"}`}>
          {items.map((t, i) => (
            <div key={i} className="flex items-center gap-2 text-base font-semibold" data-testid={`ty-no-fee-${i}`}>
              <CheckCircle size={22} weight="fill" className="shrink-0 text-emerald-400" />{t}
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
};

const Articles = ({ b, ctx }) => {
  const items = b.articles?.length ? b.articles : ctx.shared?.articles || [];
  if (!items.length) return null;
  return (
    <Section testId="ty-block-articles">
      <h2 className={H2}>{b.title || "Helpful reading"}</h2>
      <div className={`mt-5 grid gap-4 ${ctx.compact ? "" : "md:grid-cols-3"}`}>
        {items.map((a, i) => (
          <a key={i} href={a.url} target="_blank" rel="noopener noreferrer" data-testid={`ty-article-${i}`} data-ty-article="1"
             className="group overflow-hidden rounded-2xl border border-slate-200 bg-white transition-transform duration-200 hover:-translate-y-0.5">
            {a.image_url && <img src={absUrl(a.image_url)} alt="" className="h-36 w-full object-cover" />}
            <div className="p-4 font-semibold text-[#0a1929] group-hover:underline">{a.title}</div>
          </a>
        ))}
      </div>
    </Section>
  );
};

const BLOCKS = { hero: Hero, next_steps: NextStepsBlock, firm_contact: FirmContact, video: Video, testimonials: Testimonials, no_fee: NoFee, articles: Articles };

export function BlockThankYou({ blocks, ctx }) {
  return (
    <div className="bg-[#f6f3ec] pb-12" data-testid="ty-block-page">
      {blocks.filter((b) => b.enabled !== false && BLOCKS[b.type]).map((b, i) => {
        const C = BLOCKS[b.type];
        return <C key={`${b.type}-${i}`} b={b} ctx={ctx} />;
      })}
      {ctx.isTest && (
        <div className="lp-sandbox-banner" style={{ maxWidth: 420, margin: "8px auto 0" }}>SANDBOX SUBMISSION — logged as a test lead</div>
      )}
    </div>
  );
}
